import { FaTimes, FaDownload, FaExternalLinkAlt, FaImage, FaFilePdf, FaFileWord, FaFileExcel, FaVideo, FaFileAlt } from "react-icons/fa";

interface FilePreviewModalProps {
    file?:
    | File
    | {
        name: string;
        type: string;
        url: string;
    }
    | null;
    previewUrl?: string | null;
    onClose: () => void;
}

const FilePreviewModal = ({
    file,
    previewUrl,
    onClose,
}: FilePreviewModalProps) => {

    if (!file) return null;

    const isLocalFile =
        typeof File !== "undefined" && file instanceof File;

    const fileName = file.name;
    const fileType = file.type;

    // console.log(fileName, fileType)

    const url = file instanceof File
        ? previewUrl
        : file.url;

    if (!url) return null;

    const isImage =
        fileType.endsWith("jpg") ||
        fileType.endsWith("png");

    const isPdf =
        fileType.endsWith("pdf") ||
        fileName.endsWith("pdf");

    const isDocs = fileType.endsWith("docx") ||
        fileName.endsWith("docx");

    const isVideo = fileType.endsWith("video") ||
        fileName.endsWith("mp4");

    const isExcel = fileType.endsWith("xlsx") ||
        fileName.endsWith("xlsx");

    const formatLabel = isPdf
        ? "PDF DOCUMENT"
        : isDocs
            ? "WORD DOCUMENT"
            : isExcel
                ? "EXCEL SPREADSHEET"
                : isImage
                    ? "IMAGE"
                    : isVideo
                        ? "VIDEO"
                        : "FILE";
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Preview ${fileName}`}
                className="flex max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-3xl"
                onClick={(event) => event.stopPropagation()}
            >
                {/* Header */}
                <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-4 py-3 sm:px-6 sm:py-4">
                    <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isPdf
                            ? "bg-red-50 text-red-600"
                            : isDocs
                                ? "bg-blue-50 text-blue-700 w-12"
                                : isImage
                                    ? "bg-blue-50 text-blue-600"
                                    : "bg-slate-100 text-slate-600"
                            }`}
                    >
                        {isImage ? (
                            FaImage({ size: 18 })
                        ) : isPdf ? (
                            FaFilePdf({ size: 18 })
                        ) : isDocs ? (
                            FaFileWord({ size: 18 })
                        ) : isExcel ? (
                            FaFileExcel({ size: 18 })
                        ) : isVideo ? (
                            FaVideo({ size: 18 })
                        ) : (
                            FaFileAlt({ size: 18 })
                        )}
                    </div>

                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-semibold text-slate-800 sm:text-base">
                            {fileName}
                        </h3>
                        <p className="mt-0.5 text-[10px] font-semibold tracking-wider text-slate-400">
                            {formatLabel}
                        </p>
                    </div>

                   <div className="flex shrink-0 items-center gap-2">
    <a
        href={url}
        download={fileName}
        target={isLocalFile ? undefined : "_blank"}
        rel={isLocalFile ? undefined : "noopener noreferrer"}
        title="Download file"
        aria-label="Download file"
       className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
    >
        {FaDownload({ size: 13 })}
        <span className="hidden sm:inline">Download</span>
    </a>

    <button
        type="button"
        onClick={onClose}
        aria-label="Close file preview"
        className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-red-50 hover:text-red-600 sm:h-10 sm:w-10"
    >
        {FaTimes({ size: 15 })}
    </button>
</div>
                </div>

                {/* Preview area */}
                <div className="min-h-0 flex-1 overflow-auto bg-slate-50 p-3 sm:p-6">
                    {isImage ? (
                        <div className="flex min-h-[260px] items-center justify-center sm:min-h-[400px]">
                            <img
                                src={url}
                                alt={fileName}
                                className="max-h-[68dvh] max-w-full rounded-lg object-contain shadow-sm sm:max-h-[72dvh]"
                            />
                        </div>
                    ) : isPdf ? (
                        <iframe
                            src={url}
                            title={fileName}
                            className="h-[65dvh] w-full rounded-xl border border-slate-200 bg-white sm:h-[72dvh]"
                        />
                    ) : isDocs || isExcel ? (
                        <iframe
                            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
                            title={fileName}
                            className="h-[65dvh] w-full rounded-xl border border-slate-200 bg-white sm:h-[72dvh]"
                        />
                    ) : isVideo ? (
                        <div className="flex min-h-[260px] items-center justify-center rounded-xl bg-black sm:min-h-[400px]">
                            <video
                                src={url}
                                controls
                                autoPlay
                                playsInline
                                className="max-h-[68dvh] w-full rounded-xl object-contain sm:max-h-[72dvh]"
                            >
                                Your browser does not support video playback.
                            </video>
                        </div>
                    ) : (
                        <div className="flex min-h-[280px] flex-col items-center justify-center px-4 text-center sm:min-h-[400px]">
                            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200">
                                {FaDownload({ size: 24 })}
                            </div>

                            <h4 className="text-sm font-semibold text-slate-800">
                                Preview unavailable
                            </h4>

                            <p className="mt-2 max-w-xs text-xs leading-5 text-slate-500">
                                This file type cannot be displayed here.
                                Open or download it to view its contents.
                            </p>

                            <a
                                href={url}
                                download={
                                    isLocalFile ? fileName : undefined
                                }
                                target={
                                    isLocalFile ? undefined : "_blank"
                                }
                                rel={
                                    isLocalFile
                                        ? undefined
                                        : "noopener noreferrer"
                                }
                                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                            >
                                {isLocalFile ? (
                                    FaDownload({ size: 12 })
                                ) : (
                                    FaExternalLinkAlt({ size: 12 })
                                )}
                                {isLocalFile ? "Download file" : "Open file"}
                            </a>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 bg-white px-4 py-3 sm:px-6">
                    <p className="truncate text-[11px] text-slate-400">
                        {isLocalFile
                            ? "Local attachment"
                            : "Secure attachment preview"}
                    </p>

                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-xl px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                        Close preview
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilePreviewModal;
